import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const ScheduleListNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [search, setSearch] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [transportTypes, setTransportTypes] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      ScheduleStatusID: "",
      ScheduleTypeID: "",
      TransportationTypeID: "",
      AirlineID: "",
      VehicleID: "",
      ScheduleDate: "",
      FlightNo: "",
      RateOverride: "",
      DriverID: "",
    },
  });

  useEffect(() => {
    if (schedule) {
      reset({
        CompanyID: schedule.CompanyID,
        ScheduleStatusID: schedule.ScheduleStatusID,
        ScheduleTypeID: schedule.ScheduleTypeID,
        TransportationTypeID: schedule.TransportationTypeID,
        AirlineID: schedule.AirlineID,
        VehicleID: schedule.VehicleID,
        ScheduleDate: schedule.ScheduleDate
          ? schedule.ScheduleDate.split("T")[0]
          : "",
        FlightNo: schedule.FlightNo,
        RateOverride: schedule.RateOverride,
        DriverID: schedule.DriverID,
      });
    }
  }, [schedule, reset]);

  useEffect(() => {
    const fetchLookups = async () => {
      const { data: Companies } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      setCompanies(Companies || []);
      const { data: Statuses } = await supabase
        .from("ScheduleStatuses")
        .select("ScheduleStatusID, Description");
      setStatuses(Statuses || []);
      const { data: Types } = await supabase
        .from("ScheduleTypes")
        .select("ScheduleTypeID, Description");
      setTypes(Types || []);
      const { data: TransportTypes } = await supabase
        .from("ScheduleTransportationTypes")
        .select("TransportationTypeID, Description");
      setTransportTypes(TransportTypes || []);
      const { data: Airlines } = await supabase
        .from("Airlines")
        .select("AirlineID, Description");
      setAirlines(Airlines || []);
      const { data: Vehicles } = await supabase
        .from("Vehicles")
        .select("VehicleID, Model");
      setVehicles(Vehicles || []);
      const { data: Drivers } = await supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles (
            id,
            first_name,
            last_name
          )
        `
        )
        .eq("is_active", true);

      // Transform data to match expected format
      const transformedDrivers =
        Drivers?.map((driver) => ({
          DriverID: driver.id,
          FirstName: driver.driver_profiles?.[0]?.first_name || "",
          LastName: driver.driver_profiles?.[0]?.last_name || "",
          Email: driver.email,
        })) || [];

      setDrivers(transformedDrivers);
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchSchedules = async () => {
      let query = supabase
        .from("Schedule")
        .select(
          "ScheduleID, CompanyID, ScheduleStatusID, ScheduleTypeID, TransportationTypeID, AirlineID, VehicleID, ScheduleDate, FlightNo, RateOverride, DriverID",
          { count: "exact", head: false }
        )
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setScheduleData(data || []);
        setTotalCount(count || 0);
      }
    };
    fetchSchedules();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setSchedule(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      ScheduleStatusID: statuses[0] ? statuses[0].ScheduleStatusID : "",
      ScheduleTypeID: types[0] ? types[0].ScheduleTypeID : "",
      TransportationTypeID: transportTypes[0]
        ? transportTypes[0].TransportationTypeID
        : "",
      AirlineID: airlines[0] ? airlines[0].AirlineID : "",
      VehicleID: vehicles[0] ? vehicles[0].VehicleID : "",
      ScheduleDate: "",
      FlightNo: "",
      RateOverride: "",
      DriverID: drivers[0] ? drivers[0].DriverID : "",
    });
    setIsEdit(true);
    setSchedule(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setSchedule(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (schedule) {
        // Update
        const { error } = await supabase
          .from("Schedule")
          .update({
            CompanyID: data.CompanyID,
            ScheduleStatusID: data.ScheduleStatusID,
            ScheduleTypeID: data.ScheduleTypeID,
            TransportationTypeID: data.TransportationTypeID,
            AirlineID: data.AirlineID,
            VehicleID: data.VehicleID,
            ScheduleDate: data.ScheduleDate,
            FlightNo: data.FlightNo,
            RateOverride: data.RateOverride,
            DriverID: data.DriverID,
          })
          .eq("ScheduleID", schedule.ScheduleID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase.from("Schedule").insert({
          CompanyID: data.CompanyID,
          ScheduleStatusID: data.ScheduleStatusID,
          ScheduleTypeID: data.ScheduleTypeID,
          TransportationTypeID: data.TransportationTypeID,
          AirlineID: data.AirlineID,
          VehicleID: data.VehicleID,
          ScheduleDate: data.ScheduleDate,
          FlightNo: data.FlightNo,
          RateOverride: data.RateOverride,
          DriverID: data.DriverID,
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [schedule, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Company</div>
            <div className="w-35">
              <Controller
                control={control}
                name="CompanyID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.CompanyID?.message}
                  >
                    {companies.map((company) => (
                      <option key={company.CompanyID} value={company.CompanyID}>
                        {company.Name}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Status</div>
            <div className="w-35">
              <Controller
                control={control}
                name="ScheduleStatusID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.ScheduleStatusID?.message}
                  >
                    {statuses.map((status) => (
                      <option
                        key={status.ScheduleStatusID}
                        value={status.ScheduleStatusID}
                      >
                        {status.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Type</div>
            <div className="w-35">
              <Controller
                control={control}
                name="ScheduleTypeID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.ScheduleTypeID?.message}
                  >
                    {types.map((type) => (
                      <option
                        key={type.ScheduleTypeID}
                        value={type.ScheduleTypeID}
                      >
                        {type.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Transportation Type</div>
            <div className="w-35">
              <Controller
                control={control}
                name="TransportationTypeID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.TransportationTypeID?.message}
                  >
                    {transportTypes.map((type) => (
                      <option
                        key={type.TransportationTypeID}
                        value={type.TransportationTypeID}
                      >
                        {type.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Airline</div>
            <div className="w-35">
              <Controller
                control={control}
                name="AirlineID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.AirlineID?.message}
                  >
                    {airlines.map((airline) => (
                      <option key={airline.AirlineID} value={airline.AirlineID}>
                        {airline.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Vehicle</div>
            <div className="w-35">
              <Controller
                control={control}
                name="VehicleID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.VehicleID?.message}
                  >
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                        {vehicle.Model}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Driver</div>
            <div className="w-35">
              <Controller
                control={control}
                name="DriverID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.DriverID?.message}
                  >
                    {drivers.map((driver) => (
                      <option key={driver.DriverID} value={driver.DriverID}>
                        {driver.FirstName} {driver.LastName}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Schedule Date</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="ScheduleDate"
                rules={{ required: "Schedule Date is required" }}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Flight No</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="FlightNo"
                rules={{ required: "Flight No is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.FlightNo?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Rate Override</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="RateOverride"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="number"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{schedule ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Schedule List</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Company</th>
                <th className="px-4 py-2 border">Status</th>
                <th className="px-4 py-2 border">Type</th>
                <th className="px-4 py-2 border">Transport</th>
                <th className="px-4 py-2 border">Airline</th>
                <th className="px-4 py-2 border">Vehicle</th>
                <th className="px-4 py-2 border">Driver</th>
                <th className="px-4 py-2 border">Schedule Date</th>
                <th className="px-4 py-2 border">Flight No</th>
                <th className="px-4 py-2 border">Rate Override</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {scheduleData &&
                scheduleData.map((type) => (
                  <tr key={type.ScheduleID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{type.ScheduleID}</td>
                    <td className="px-4 py-2 border">
                      {companies.find((c) => c.CompanyID === type.CompanyID)
                        ?.Name || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {statuses.find(
                        (s) => s.ScheduleStatusID === type.ScheduleStatusID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {types.find(
                        (t) => t.ScheduleTypeID === type.ScheduleTypeID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {transportTypes.find(
                        (t) =>
                          t.TransportationTypeID === type.TransportationTypeID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {airlines.find((a) => a.AirlineID === type.AirlineID)
                        ?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {vehicles.find((v) => v.VehicleID === type.VehicleID)
                        ?.Model || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {drivers.find((d) => d.DriverID === type.DriverID)
                        ? `${
                            drivers.find((d) => d.DriverID === type.DriverID)
                              .FirstName
                          } ${
                            drivers.find((d) => d.DriverID === type.DriverID)
                              .LastName
                          }`
                        : ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {type.ScheduleDate ? type.ScheduleDate.split("T")[0] : ""}
                    </td>
                    <td className="px-4 py-2 border">{type.FlightNo}</td>
                    <td className="px-4 py-2 border">{type.RateOverride}</td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setSchedule(type);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleListNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "FlightNo",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"FlightNo"}>Flight No</option>
              <option value={"ScheduleDate"}>Schedule Date</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "FlightNo", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
